import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { Globe, Camera } from "lucide-react";

import {
  AddCountryAction,
  UpdateCountryAction,
} from "store/middlewares/dataManagement/country";
import { Input, Label, Button } from "components/ui";
import { SubmitLoaderButton } from "components/atoms";
import { openToast } from "store/slices/common/toast.slice";
import { closeAdminModal } from "store/slices/common/adminModal.slice";
import DefaultImg from "assets/images/PNG/defaultImage.jpg";

function ModalEditCountry({ row }) {
  const dispatch = useDispatch();
  const [previewImage, setPreviewImage] = useState();
  const [formError, setFormError] = useState(false);
  const { settings } = useSelector((state) => state.settings);

  useEffect(() => {
    if (row) {
      setPreviewImage(row?.image_url);
    }
  }, [row]);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];

    if (!file) return;

    const fileSizeLimit = settings?.max_file_size * 1024 * 1024;

    // File type validation
    if (!file.type.match(/^image\/(png|jpeg|jpg|svg(\+xml)?)$/)) {
      dispatch(
        openToast({
          type: "error",
          message: "Only images (PNG, JPG, JPEG, SVG) are allowed.",
        })
      );
      return;
    }

    // File size validation
    if (file.size > fileSizeLimit) {
      dispatch(
        openToast({
          type: "error",
          message: `Please upload images less than ${settings?.max_file_size}MB.`,
        })
      );
      return;
    }

    // Corrupted image validation
    const isValidImage = await new Promise((resolve) => {
      const img = new Image();
      const imageUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(imageUrl);
        resolve(true);
      };

      img.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        resolve(false);
      };

      img.src = imageUrl;
    });

    if (!isValidImage) {
      dispatch(
        openToast({
          type: "error",
          message: "This is corrupted image please upload valid image.",
        })
      );

      event.target.value = "";
      return;
    }

    // Valid image
    const reader = new FileReader();

    reader.onload = () => {
      formik.setFieldValue("image", reader.result);
      formik.setFieldValue("file", file);
      formik.setFieldError("image", "");
      formik.setFieldTouched("image", false);
      setPreviewImage(reader.result);
    };

    reader.readAsDataURL(file);
  };

  const handleSubmit = (values) => {
    const updateValue = { ...values, name: values.name.trim() };

    let tempObj = { ...updateValue, countryCode: `+${values.countryCode}` };
    if (row) {
      tempObj = {
        ...tempObj,
        id: row?.id,
        image: row?.image,
      };
      dispatch(
        UpdateCountryAction({
          ...tempObj,
          onSuccess: () => formik.setSubmitting(false),
          onError: () => setFormError(true),
        })
      );
    } else {
      dispatch(
        AddCountryAction({
          ...tempObj,
          onSuccess: () => formik.setSubmitting(false),
          onError: () => setFormError(true),
        })
      );
    }
  };

  const formik = useFormik({
    initialValues: {
      name: row?.name ? row?.name : "",
      countryCode: row?.country_code?.replace("+", "") ?? "",
      file: null,
      image: row?.image_url ?? "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Country name is required"),
      countryCode: Yup.string().required("Country code is required"),
      image: Yup.string().required("Image is required"),
    }),
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  return (
    <form onSubmit={formik.handleSubmit} className="grid gap-5">
      {/* Title with icon */}
      <div className="flex items-center gap-3.5">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
          <Globe className="size-5" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-bold text-foreground">
          {row ? "Edit country" : "Add country"}
        </h2>
      </div>

      {/* Photo */}
      <div className="grid justify-items-center gap-1">
        <div className="relative">
          <div className="size-24 overflow-hidden rounded-full border border-gray-200">
            <img
              src={previewImage ?? DefaultImg}
              alt="Flag"
              className="h-full w-full object-cover"
            />
          </div>
          <input
            type="file"
            name="file"
            accept="image/png, image/jpeg, image/jpg, image/svg+xml"
            id="editimg"
            onChange={handleFileChange}
            hidden
          />
          <label
            htmlFor="editimg"
            title="Upload image"
            className="absolute bottom-0 right-0 flex size-8 cursor-pointer items-center justify-center rounded-full border border-border bg-white text-foreground shadow-sm"
          >
            <Camera className="size-4" aria-hidden="true" />
            <span className="sr-only">Upload image</span>
          </label>
        </div>
        {formik.errors.image && formik.touched.image && (
          <div className="text-[0.8125rem] text-red-500">
            {formik.errors.image}
          </div>
        )}
      </div>

      {/* Inputs */}
      <div className="grid gap-4">
        <div>
          <Label htmlFor="name">Country Name</Label>
          <Input
            id="name"
            className="!py-0 w-full mt-2"
            variant="lg"
            error={formik.errors.name && formik.touched.name}
            errorMsg={formik.errors.name}
            value={formik.values.name}
            type="text"
            name="name"
            maxLength={50}
            isSpace={true}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            isTextOnly={true}
            placeholder="Enter name"
          />
        </div>

        <div>
          <Label htmlFor="countryCode">Country Code</Label>
          <Input
            id="countryCode"
            className="!py-0 w-full mt-2"
            variant="lg"
            error={formik.errors.countryCode && formik.touched.countryCode}
            errorMsg={formik.errors.countryCode}
            value={formik.values.countryCode}
            type="text"
            isSpace={true}
            isNumeric={true}
            name="countryCode"
            onChange={formik.handleChange}
            maxLength={5}
            onBlur={formik.handleBlur}
            placeholder="Enter country code"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => dispatch(closeAdminModal())}
        >
          Cancel
        </Button>
        <SubmitLoaderButton
          btnText={row ? "Update" : "Add"}
          isDisable={formik.isSubmitting && !formError}
        />
      </div>
    </form>
  );
}

export default ModalEditCountry;